import { useCallback, useEffect, useState } from 'react'
import { Script } from '@calendly/react-script-tag'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'astroturf/react'
import { SafeSuspense } from '@calendly/safe-suspense'
import { buildQuery } from '@calendly/http-tools'
const fontWeightSemibold = 'var(--fontWeightSemibold, 600)'

import Fieldset from 'components/form/fieldset'
import Info from 'components/enter_details/payment/info'
import { StripeForm } from 'components/enter_details/payment/stripe_form'
import StripeContainer from 'components/payment/stripe/container'
import PaypalForm from 'components/enter_details/payment/paypal_form'
import ErrorMessage from 'components/form/error_message'
import { Settings } from 'services/settings'
import { formFieldUpdated } from 'actions/scheduling'
import { eventTypeSelector, useValidateCouponMutation } from 'root-api'
import { useI18n } from 'locales'
import Coupon from 'components/enter_details/payment/coupon'
import InfoCoupon from 'components/enter_details/payment/info_coupon'
import { stripeClientSecretSelector } from 'selectors/payment'

import { parseAmountFromSymbol, calculateFinalPaymentAmount } from './booking_code_utils'
import _Information from "index-Information.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/index.js?styleId=Information";


const Information = /*#__PURE__*/styled('div', null, {
  displayName: "Information",
  styles: _Information
})

const buildPaypalSdkQuery = (externalUserId, providerLocale, currency) => buildQuery({
  'client-id': Settings.paypal.client_id,
  'merchant-id': externalUserId,
  intent: 'authorize',
  commit: 'false',
  locale: providerLocale,
  currency,
  'disable-funding': 'credit,card'
})

const PaymentForm = ({ registerApi }) => {
  const { i18n } = useI18n()
  const {
    payment_method: { provider, provider_locale: providerLocale, currency } = {},
    payment_account: { external_user_id: externalUserId } = {}
  } = useSelector(eventTypeSelector)

  const renderTranslatedErrorMessage = useCallback(() => (
    <ErrorMessage message={i18n.t('errors.billing.card.payment_form_not_loaded')} />
  ), [i18n])

  const eventScheduled = useSelector((state) => Object.keys(state.scheduling.event).length !== 0)

  // Prevent form re-rendering when an event has been scheduled
  if (eventScheduled) return null

  return (
    <SafeSuspense onError={renderTranslatedErrorMessage}>
      {provider === 'stripe' && (
        <StripeContainer stripeAccount={externalUserId}>
          <StripeForm registerApi={registerApi} />
        </StripeContainer>
      )}
      {provider === 'paypal' && (
        <Script
          src={`https://www.paypal.com/sdk/js?${buildPaypalSdkQuery(externalUserId, providerLocale, currency)}`}
          data-partner-attribution-id="Calendly_SP"
        >
          <PaypalForm registerApi={registerApi} />
        </Script>
      )}
    </SafeSuspense>
  )
}

const Payment = ({ registerApi, inviteeEmail }) => {
  const eventType = useSelector(eventTypeSelector)
  const hasActiveBookingCodes = eventType.has_active_booking_codes
  const isStripePayment = eventType.payment_method?.provider === 'stripe'
  const [showCouponInput, setShowCouponInput] = useState(true)
  const [paymentElementKey, setPaymentElementKey] = useState(0)
  const stripeClientSecret = useSelector(stripeClientSecretSelector)
  const { i18n } = useI18n()
  const {
    payment_method: {
      amount_with_symbol: amountWithSymbol, currency, payment_terms: paymentTerms
    } = {},
    uuid: eventTypeUuid
  } = eventType

  const [
    validateCoupon,
    { data: couponData }
  ] = useValidateCouponMutation({
    fixedCacheKey: 'couponData'
  })

  const dispatch = useDispatch()

  const finalPaymentResult = calculateFinalPaymentAmount(amountWithSymbol, couponData)
  const finalPaymentAmount = parseAmountFromSymbol(finalPaymentResult).amount

  useEffect(() => {
    dispatch(formFieldUpdated('payment', 1))
  }, [dispatch])

  // Register a no-op payment API for $0 amounts since no PaymentForm will be rendered
  useEffect(() => {
    if (finalPaymentAmount === 0 && registerApi) {
      registerApi({
        createPayment: () => Promise.resolve(true),
        handleCardAction: () => Promise.resolve(true)
      })
    }
  }, [finalPaymentAmount, registerApi])

  return (
    <>
      <Information>
        {i18n.t('booking.templates.payment.information')}
        {' *'}
      </Information>
      {hasActiveBookingCodes && isStripePayment && showCouponInput
        && (
        <Coupon
          inviteeEmail={inviteeEmail}
          onCouponApplied={() => {
            setShowCouponInput(false)
            setTimeout(() => {
              setPaymentElementKey((prev) => prev + 1)
            }, 100)
          }}
        />
        )}
      <Fieldset>
        {couponData && !showCouponInput
          ? (
            <InfoCoupon
              amountWithSymbol={amountWithSymbol}
              currency={currency}
              paymentTerms={paymentTerms}
              onRemoveCoupon={async () => {
                await validateCoupon({
                  eventTypeUuid,
                  clientSecret: stripeClientSecret.value
                })
                setShowCouponInput(true)
                setTimeout(() => {
                  setPaymentElementKey((prev) => prev + 1)
                }, 100)
              }}
            />
          ) : (
            <Info
              amountWithSymbol={amountWithSymbol}
              currency={currency}
              paymentTerms={paymentTerms}
            />
          )}
        {finalPaymentAmount > 0 && <PaymentForm key={paymentElementKey} registerApi={registerApi} />}
      </Fieldset>
    </>
  )
}

export default Payment
